import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { User, LogOut, Settings } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useProfile } from '../../contexts/ProfileContext';

interface TopbarProps {
  pageTitle: string;
}

const Topbar: React.FC<TopbarProps> = ({ pageTitle }) => {
  const { user, logout } = useAuth();
  const { profile } = useProfile();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const displayName = profile?.full_name || user?.name;

  return (
    <div className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6">
      <div className="flex items-center">
        <h1 className="text-2xl font-bold text-gray-900 font-display tracking-tight">{pageTitle}</h1>
      </div>

      <div className="flex items-center space-x-4">
        {/* User Menu */}
        <div className="relative" ref={userMenuRef}>
          <div
            className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 rounded-lg p-2 transition-colors"
            onClick={() => setShowUserMenu(!showUserMenu)}
          >
            <div className="text-right">
              <p className="text-sm font-medium text-gray-900">{displayName}</p>
              <p className="text-xs text-gray-500">{user?.email}</p>
            </div>
            {profile?.profile_picture_url ? (
              <img
                src={profile.profile_picture_url}
                alt={displayName || 'Profile'}
                className="w-8 h-8 rounded-full object-cover border-2 border-[#05E0D1] shadow-[0_0_8px_rgba(5,224,209,0.5),0_0_16px_rgba(5,224,209,0.25)]"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                  if (fallback) fallback.style.display = 'flex';
                }}
              />
            ) : null}
            <div
              className={`w-8 h-8 bg-blue-600 rounded-full items-center justify-center border-2 border-[#05E0D1] shadow-[0_0_8px_rgba(5,224,209,0.5),0_0_16px_rgba(5,224,209,0.25)] ${profile?.profile_picture_url ? 'hidden' : 'flex'}`}
            >
              <User size={16} className="text-white" />
            </div>
          </div>

          {/* Dropdown Menu */}
          {showUserMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10 border border-gray-200">
              {/* Profile Info */}
              <div className="px-4 py-2 border-b border-gray-100">
                <p className="text-xs text-gray-500">Signed in as:</p>
                <p className="text-sm font-medium text-gray-900 truncate">{user?.email}</p>
              </div>

              {/* Settings */}
              <Link
                to="/settings"
                className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                onClick={() => setShowUserMenu(false)}
              >
                <Settings size={16} />
                <span>Settings</span>
              </Link>

              {/* Logout */}
              <button
                onClick={() => {
                  logout();
                  setShowUserMenu(false);
                }}
                className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors text-left"
              >
                <LogOut size={16} />
                <span>Sign out</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Topbar;
